import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import type {
  Invoice,
  InvoiceFilters,
  CreateInvoiceInput,
  UpdateInvoiceInput,
} from '@/types/invoice';

interface InvoicesListResponse {
  invoices: Invoice[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Fetch invoices with filters
 */
export function useInvoices(filters?: InvoiceFilters) {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.client_id) params.append('client_id', filters.client_id);
      if (filters?.payment_id) params.append('payment_id', filters.payment_id);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.financial_year) params.append('financial_year', filters.financial_year);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));

      const { data } = await api.get<InvoicesListResponse>(
        `/api/invoices?${params.toString()}`
      );

      return data;
    },
  });
}

/**
 * Fetch a single invoice by ID
 */
export function useInvoice(invoiceId?: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      if (!invoiceId) throw new Error('Invoice ID is required');

      const { data } = await api.get<Invoice>(`/api/invoices/${invoiceId}`);
      return data;
    },
    enabled: !!invoiceId,
  });
}

/**
 * Create a new invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateInvoiceInput) => {
      const { data } = await api.post<Invoice>('/api/invoices', input);
      return data;
    },
    onSuccess: (newInvoice) => {
      // Invalidate invoice lists
      queryClient.invalidateQueries({ queryKey: ['invoices'] });

      // Invalidate specific client's invoices
      queryClient.invalidateQueries({
        queryKey: ['invoices', { client_id: newInvoice.client_id }],
      });

      // Invalidate payment's invoices if linked
      if (newInvoice.payment_id) {
        queryClient.invalidateQueries({
          queryKey: ['invoices', { payment_id: newInvoice.payment_id }],
        });
      }
    },
  });
}

/**
 * Update an existing invoice (draft only)
 */
export function useUpdateInvoice(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateInvoiceInput) => {
      const { data } = await api.put<Invoice>(`/api/invoices/${invoiceId}`, input);
      return data;
    },
    onSuccess: (updatedInvoice) => {
      // Update specific invoice cache
      queryClient.setQueryData(['invoice', invoiceId], updatedInvoice);

      // Invalidate invoice lists
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Delete an invoice (soft delete)
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      await api.delete(`/api/invoices/${invoiceId}`);
      return invoiceId;
    },
    onSuccess: (invoiceId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['invoice', invoiceId] });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Send invoice via WhatsApp
 */
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        `/api/invoices/${invoiceId}/send`,
        {}
      );
      return { invoiceId, ...data };
    },
    onSuccess: ({ invoiceId }) => {
      // Refetch the invoice to get updated status
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Get invoice PDF URL
 */
export function getInvoicePDFUrl(invoiceId: string): string {
  return `/api/invoices/${invoiceId}/pdf`;
}
